import {
  esc,
  formatMoney,
  invoiceDownloadUrl,
  listDeposits,
  listInvoices,
  listWithdrawals,
  toPaymentError,
} from '../../core/payments';
import type { DepositOrder, PlayerInvoice, Withdrawal } from '../../core/payments';
import { t, type Translations } from '../../engine/i18n/i18n';
import { icon } from '../../engine/icons';

/**
 * Payment history for the shop: deposits, withdrawals and invoices, each with
 * a status badge. The three lists load independently so one failing endpoint
 * (e.g. withdrawals switched off) leaves the other two usable.
 *
 * Invoice links are same-origin downloads authorised by the session cookie;
 * they carry `download` and `rel="noopener"` and are excluded from the SPA
 * router by the browser treating them as file downloads.
 */

export interface HistoryPanelOptions {
  root: HTMLElement;
  onError: (err: Error) => void;
  take?: number;
}

type Tone = 'ok' | 'wait' | 'bad' | 'neutral';

const STATUS: Record<string, { label: keyof Translations; tone: Tone }> = {
  PENDING: { label: 'shop.history.status.PENDING', tone: 'wait' },
  PROCESSING: { label: 'shop.history.status.PROCESSING', tone: 'wait' },
  APPROVED: { label: 'shop.history.status.APPROVED', tone: 'ok' },
  PAID: { label: 'shop.history.status.PAID', tone: 'ok' },
  ISSUED: { label: 'shop.history.status.ISSUED', tone: 'ok' },
  DECLINED: { label: 'shop.history.status.DECLINED', tone: 'bad' },
  ERROR: { label: 'shop.history.status.ERROR', tone: 'bad' },
  FAILED: { label: 'shop.history.status.FAILED', tone: 'bad' },
  REJECTED: { label: 'shop.history.status.REJECTED', tone: 'bad' },
  EXPIRED: { label: 'shop.history.status.EXPIRED', tone: 'neutral' },
  MISMATCH: { label: 'shop.history.status.MISMATCH', tone: 'wait' },
  PARTIALLY_REFUNDED: { label: 'shop.history.status.PARTIALLY_REFUNDED', tone: 'neutral' },
  REFUNDED: { label: 'shop.history.status.REFUNDED', tone: 'neutral' },
  VOID: { label: 'shop.history.status.VOID', tone: 'neutral' },
};

const TONE_CLS: Record<Tone, string> = {
  ok: 'bg-success/15 text-success border-success/40',
  wait: 'bg-gold/10 text-gold border-gold/40',
  bad: 'bg-error/10 text-error border-error/40',
  neutral: 'bg-surface-2 text-text-dim border-border',
};

export function statusBadgeHtml(status: string): string {
  const known = STATUS[status];
  const s = known
    ? { label: t(known.label), tone: known.tone }
    : { label: status.charAt(0) + status.slice(1).toLowerCase(), tone: 'neutral' as Tone };
  return `<span data-status="${esc(status)}" class="shrink-0 px-2 py-0.5 rounded border text-[10px] uppercase tracking-wide ${TONE_CLS[s.tone]}">${esc(s.label)}</span>`;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

function invoiceLink(number: string): string {
  return `<a data-role="invoice" href="${esc(invoiceDownloadUrl(number))}" download rel="noopener"
    title="${esc(t('shop.history.invoiceLink', { number }))}" aria-label="${esc(t('shop.history.invoiceLink', { number }))}"
    class="shrink-0 min-h-11 inline-flex items-center gap-1.5 px-3 text-xs text-gold hover:text-gold-bright touch-manipulation"><span class="w-4 h-4 inline-block" aria-hidden="true">${icon('download')}</span><span>${esc(t('shop.history.download'))}</span></a>`;
}

function rowHtml(opts: { id: string; title: string; sub: string; status: string; trailing?: string }): string {
  return `
    <li data-row="${esc(opts.id)}" class="glass-panel px-3 py-2 flex items-center gap-2 flex-wrap">
      <span class="flex-1 min-w-[10rem]">
        <span class="block text-sm text-text">${esc(opts.title)}</span>
        <span class="block text-xs text-text-dim">${esc(opts.sub)}</span>
      </span>
      ${statusBadgeHtml(opts.status)}
      ${opts.trailing ?? ''}
    </li>`;
}

function depositRow(d: DepositOrder): string {
  return rowHtml({
    id: d.orderId,
    title: t('shop.history.depositTitle', { amount: formatMoney(d.amountCents, d.currency) }),
    sub: t(d.credited ? 'shop.history.depositSubCredited' : 'shop.history.depositSub', { date: shortDate(d.createdAt), method: d.method }),
    status: d.status,
    trailing: d.invoiceNumber ? invoiceLink(d.invoiceNumber) : '',
  });
}

function withdrawalRow(w: Withdrawal): string {
  const det = (w.details ?? {}) as Record<string, unknown>;
  const last4 = typeof det.last4 === 'string' ? ` •••• ${det.last4}` : '';
  return rowHtml({
    id: w.id,
    title: t('shop.history.withdrawalTitle', { amount: formatMoney(w.amountCents, w.currency) }),
    sub: `${t('shop.history.withdrawalSub', { date: shortDate(w.createdAt), method: w.method, last4, net: formatMoney(w.netCents, w.currency) })}${w.reason ? ` · ${w.reason}` : ''}`,
    status: w.status,
    trailing: w.invoiceNumber ? invoiceLink(w.invoiceNumber) : '',
  });
}

function invoiceRow(i: PlayerInvoice): string {
  return rowHtml({
    id: i.id,
    title: t(i.type === 'OUT' ? 'shop.history.payoutInvoiceTitle' : 'shop.history.invoiceTitle', { number: i.number, total: formatMoney(i.totalCents, i.currency) }),
    sub: `${shortDate(i.issuedAt)} · ${i.description}`,
    status: i.status,
    trailing: invoiceLink(i.number),
  });
}

function section(title: string, key: string, body: string): string {
  return `
    <section data-section="${key}" class="flex flex-col gap-2">
      <h3 class="font-heading text-[11px] text-text-dim uppercase tracking-[0.18em]">${esc(title)}</h3>
      ${body}
    </section>`;
}

function listOrNote<T>(res: PromiseSettledResult<T[]>, render: (x: T) => string, empty: string): string {
  if (res.status === 'rejected') {
    return `<p class="text-xs text-error" role="alert">${esc(toPaymentError(res.reason).message)}</p>`;
  }
  if (res.value.length === 0) return `<p class="text-sm text-text-dim">${esc(empty)}</p>`;
  return `<ul class="flex flex-col gap-2" role="list">${res.value.map(render).join('')}</ul>`;
}

export function mountHistoryPanel(opts: HistoryPanelOptions): { destroy(): void; refresh(): Promise<void> } {
  const { root } = opts;
  let destroyed = false;

  root.innerHTML = `<div class="glass-panel p-4 text-center text-sm text-text-dim" role="status">${t('shop.history.loading')}</div>`;

  async function refresh(): Promise<void> {
    const [deps, wds, invs] = await Promise.allSettled([
      listDeposits(opts.take ?? 20),
      listWithdrawals(),
      listInvoices(1).then((r) => r.items),
    ]);
    if (destroyed) return;
    const sortNewest = <T extends { createdAt: string }>(r: PromiseSettledResult<T[]>): PromiseSettledResult<T[]> =>
      r.status === 'fulfilled'
        ? { status: 'fulfilled', value: [...r.value].sort((a, b) => b.createdAt.localeCompare(a.createdAt)) }
        : r;
    root.innerHTML = `
      <div class="flex flex-col gap-5" data-history>
        ${section(t('shop.history.deposits'), 'deposits', listOrNote(sortNewest(deps), depositRow, t('shop.history.noDeposits')))}
        ${section(t('shop.history.withdrawals'), 'withdrawals', listOrNote(sortNewest(wds), withdrawalRow, t('shop.history.noWithdrawals')))}
        ${section(t('shop.history.invoices'), 'invoices', listOrNote(sortNewest(invs), invoiceRow, t('shop.history.noInvoices')))}
      </div>`;
    if (deps.status === 'rejected' && wds.status === 'rejected' && invs.status === 'rejected') {
      opts.onError(toPaymentError(deps.reason));
    }
  }

  void refresh();

  return {
    destroy() {
      destroyed = true;
      root.innerHTML = '';
    },
    refresh,
  };
}
